import React, { Component } from "react";
import { Button, LinearProgress, Typography, WithStyles, createStyles, Theme } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { Game } from "../../types/game";
import GameActions from "../../actions/games";
import GamesApi from "../../api/games";
import { GameAdditionalOrganisersTable } from "./GameAdditionalOrganiserTable";
import { connect } from "react-redux";
import { Rules, User, UserBrifObj } from "../../types/user";
import Loading from "../Loading";
import { AxiosError } from "axios";
import { ErrorOnlyCallback } from "../../types/actions";
import { GlobalState } from "../../types/globalState";
import BorderedBox from "../SharedComponents/BorderedBox";
import { showSnackbar } from "../SharedComponents/Notifier";
import { getGameOwnerId, userIsGameOwner, canBeGameOwner, GAME_OWNER_NOT_ORGANISER_TYPE_ERROR } from "../../utils/gameOrganiser";

interface Props extends WithStyles<typeof styles> {
  getGameById(
    id: string,
    queryType?: string,
    updateRedux?: boolean,
    callback?: (err: AxiosError | null, data: Game) => void,
  ): void;
  addOrganiser(gameId: string, playerId: string, callback?: ErrorOnlyCallback): void;
  removeOrganiser(gameId: string, playerId: string, callback?: ErrorOnlyCallback): void;
  game: Game;
  user: User;
  viewOnly?: boolean;
}

interface State {
  game: Game;
  pendingOwnerId: string;
  serverOwnerId: string;
  ownershipApplyLoading: boolean;
  ownershipError: string | null;
}

function organiserDisplayName(organiser: UserBrifObj | undefined): string {
  if (!organiser) return "";
  const s = [organiser.name, organiser.lastName].filter(Boolean).join(" ").trim();
  return s || organiser.username || "";
}

function organisersWithOwner(game: Game): UserBrifObj[] {
  const organisers = [...(game.organizers || [])];
  const ownerId = getGameOwnerId(game);
  if (!ownerId || organisers.some(o => o && String(o._id) === ownerId)) {
    return organisers;
  }

  const ownerFromField =
    typeof game.owner === "object" && game.owner != null && (game.owner as UserBrifObj)._id
      ? (game.owner as UserBrifObj)
      : undefined;

  if (ownerFromField) {
    return [ownerFromField, ...organisers];
  }

  if (game.organiser) {
    return [{ ...game.organiser, _id: ownerId } as UserBrifObj, ...organisers];
  }

  return organisers;
}

class GameAdditionalOrganisersManager extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const ownerId = getGameOwnerId(props.game) || "";
    this.state = {
      game: props.game,
      pendingOwnerId: ownerId,
      serverOwnerId: ownerId,
      ownershipApplyLoading: false,
      ownershipError: null,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.game._id !== this.props.game._id) {
      const ownerId = getGameOwnerId(this.props.game) || "";
      this.setState({
        game: this.props.game,
        pendingOwnerId: ownerId,
        serverOwnerId: ownerId,
        ownershipError: null,
      });
    }
  }

  syncOwnerFromGame = (game: Game) => {
    const ownerId = getGameOwnerId(game) || "";
    this.setState({
      game,
      pendingOwnerId: ownerId,
      serverOwnerId: ownerId,
      ownershipError: null,
    });
  };

  reload = () => {
    this.props.getGameById(this.props.game._id, undefined, undefined, (err, game) => {
      if (!err) this.syncOwnerFromGame(game);
    });
  };

  _isOwnershipDirty = () => String(this.state.pendingOwnerId) !== String(this.state.serverOwnerId);

  _rejectInvalidOwnerSelection = (msg: string) => {
    this.setState({
      pendingOwnerId: this.state.serverOwnerId,
      ownershipError: msg,
    });
    showSnackbar(msg);
  };

  _handlePendingOwnerIdChange = (id: string) => {
    const organisers = organisersWithOwner(this.state.game);
    const candidate = organisers.find(o => o && String(o._id) === String(id));
    const candidateRule = (candidate as { rule?: string } | undefined)?.rule;
    if (candidateRule && !canBeGameOwner({ rule: candidateRule })) {
      this._rejectInvalidOwnerSelection(GAME_OWNER_NOT_ORGANISER_TYPE_ERROR);
      return;
    }
    this.setState({ pendingOwnerId: id, ownershipError: null });
  };

  _applyOwnership = async () => {
    const { game, pendingOwnerId, serverOwnerId, ownershipApplyLoading } = this.state;
    if (ownershipApplyLoading || !this._isOwnershipDirty()) return;

    const ownerId = String(pendingOwnerId);
    const organisers = organisersWithOwner(game);
    if (!organisers.some(o => o && String(o._id) === ownerId)) {
      const msg = "The owner must be in the list. Add that person as an organiser, or set another owner with the switch.";
      this._rejectInvalidOwnerSelection(msg);
      return;
    }

    const candidate = organisers.find(o => o && String(o._id) === ownerId);
    const candidateRule = (candidate as { rule?: string } | undefined)?.rule;
    if (candidateRule && !canBeGameOwner({ rule: candidateRule })) {
      this._rejectInvalidOwnerSelection(GAME_OWNER_NOT_ORGANISER_TYPE_ERROR);
      return;
    }

    this.setState({ ownershipApplyLoading: true, ownershipError: null });
    try {
      await GamesApi.transferOwner(game._id, ownerId);
      this.setState({
        ownershipApplyLoading: false,
        ownershipError: null,
        serverOwnerId: ownerId,
      });
      showSnackbar("Game ownership updated");
      this.reload();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        (err?.message === "Network Error"
          ? "API is unreachable. Ensure the backend is running on the configured API URL."
          : null) ||
        err?.message ||
        "Update failed";
      const pretty = typeof msg === "string" ? msg : "Update failed";
      const resetSelection =
        pretty === GAME_OWNER_NOT_ORGANISER_TYPE_ERROR ||
        pretty === "The new owner must be listed as a game organiser." ||
        pretty === "User not found.";
      this.setState({
        ownershipApplyLoading: false,
        ownershipError: pretty,
        pendingOwnerId: resetSelection ? serverOwnerId : this.state.pendingOwnerId,
      });
      showSnackbar(pretty);
    }
  };

  render() {
    const { game, pendingOwnerId, serverOwnerId, ownershipApplyLoading, ownershipError } = this.state;
    const { user, viewOnly, classes } = this.props;
    if (!game) return <LinearProgress />;

    const organisers = organisersWithOwner(game);
    const canSetGameOwner = userIsGameOwner(game, user._id) || user.rule === Rules.Admin;
    const pendingOwner = organisers.find(o => o && String(o._id) === String(pendingOwnerId));
    const serverOwner = organisers.find(o => o && String(o._id) === String(serverOwnerId));
    const ownerLabel = organiserDisplayName(pendingOwner) || organiserDisplayName(serverOwner) || "Unknown";

    return (
      <>
        <Loading color="primary" />
        <BorderedBox title={"Access and ownership"} variant={"h6"}>
          <Typography variant="body2" color="textSecondary" component="p" style={{ marginBottom: 6 }}>
            Everyone listed can help run this game. The owner receives payments for paid games and is one of these
            people, set only with the switch.
          </Typography>
          <Typography variant="body2" color="textSecondary" component="p" style={{ marginBottom: 10 }}>
            Owner: <strong>{ownerLabel}</strong>
            {` · On file: ${organisers.length} organiser(s)`}
          </Typography>
          <GameAdditionalOrganisersTable
            organisers={organisers}
            viewOnly={viewOnly}
            onAdd={(userObj: UserBrifObj) =>
              this.props.addOrganiser(game._id, userObj._id, err => !err && this.reload())
            }
            onDelete={(userObj: UserBrifObj) =>
              this.props.removeOrganiser(game._id, userObj._id, err => !err && this.reload())
            }
            ruleSharing={{
              showOwnerColumn: true,
              ownerId: pendingOwnerId,
              onOwnerIdChange: this._handlePendingOwnerIdChange,
              canEditOwner: canSetGameOwner && !viewOnly,
              tableFrame: true,
            }}
          />
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {!canSetGameOwner ? (
              <Typography variant="body2" color="textSecondary">
                Only the current owner (or an admin) can change who owns this game. You can still change the organiser
                list if you may edit the game.
              </Typography>
            ) : null}
            {ownershipError ? (
              <Typography variant="caption" color="error" component="p">
                {ownershipError}
              </Typography>
            ) : null}
            {!viewOnly ? (
              <div className={classes.applyRow}>
                <Button
                  className={classes.applyButton}
                  variant="contained"
                  color="primary"
                  onClick={this._applyOwnership}
                  disabled={!this._isOwnershipDirty() || ownershipApplyLoading}
                >
                  {ownershipApplyLoading ? "Applying" : "Apply changes"}
                </Button>
                {this._isOwnershipDirty() ? (
                  <Typography variant="caption" color="textSecondary" component="span" className={classes.applyHint}>
                    Unsaved owner change. Use Apply to transfer ownership to the selected organiser.
                  </Typography>
                ) : null}
              </div>
            ) : null}
          </div>
        </BorderedBox>
      </>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    applyRow: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(1),
      marginTop: theme.spacing(0.5),
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        alignItems: "stretch",
      },
    },
    applyButton: {
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    applyHint: {
      maxWidth: 360,
      [theme.breakpoints.down("xs")]: {
        maxWidth: "none",
      },
    },
  });

const mapStateToProps = (state: GlobalState) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  getGameById: GameActions.getById,
  addOrganiser: GameActions.addOrganiser,
  removeOrganiser: GameActions.removeOrganiser,
})(withStyles(styles)(GameAdditionalOrganisersManager));
